/**
 * Onglet "Action" de la fiche GRV : les trois façons de faire évoluer un GRV, chacune tracée dans
 * sa fiche de vie (`docs/DECISIONS.md` D89) — une opération de maintenance (contrôle, incident,
 * intervention, panne, remplacement de pièce…), un changement de tracker, un changement de statut
 * opérationnel. Distinctes l'une de l'autre : une opération de maintenance ne change ni le tracker
 * ni le statut, et réciproquement (`docs/DECISIONS.md` D87).
 *
 * Seul cet onglet permet de créer un événement — `OngletFicheDeVie.tsx` n'en est plus que
 * l'historique de consultation, et `OngletInformations.tsx` ne propose plus les formulaires de
 * tracker ni de statut.
 */
import { useState } from "react";

import { ErreurApi } from "../../shared/api/erreurs";
import type { FicheGrv } from "../../shared/api/types";
import { useAjouterIntervention, useTypesEvenements } from "../maintenance/requetes";
import { optionsAvecValeurCourante } from "./referentielFiche";
import { useAssocierTracker, useChangerStatut, useStatutsOperationnels } from "./requetes";

// Ces deux types ont leur propre formulaire dédié ci-dessous, avec sa propre traçabilité
// spécifique (ancien/nouveau tracker, ancien/nouveau statut) : les proposer aussi dans le menu
// déroulant générique des opérations de maintenance ferait doublon et perdrait cette précision.
const TYPES_EXCLUS_DES_OPERATIONS = new Set(["changement_tracker", "changement_statut"]);

function aujourdHui(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Même ancrage à midi UTC que l'ancien formulaire de `OngletFicheDeVie.tsx` (`docs/DECISIONS.md`
 * D74) : un jour calendaire qui ne bouge pas selon le fuseau d'affichage. */
function versDateEvenementEnvoi(valeurDate: string): string {
  return `${valeurDate}T12:00:00+00:00`;
}

export function OngletAction({ fiche }: { fiche: FicheGrv }) {
  return (
    <div className="grille-cartes">
      <div className="carte">
        <h3>Opération de maintenance</h3>
        <FormulaireOperationMaintenance numero={fiche.numero_grv} />
      </div>
      <div className="carte">
        <h3>Changement de tracker</h3>
        <FormulaireChangementTracker fiche={fiche} />
      </div>
      <div className="carte">
        <h3>Changement de statut</h3>
        <FormulaireChangementStatut fiche={fiche} />
      </div>
    </div>
  );
}

function FormulaireOperationMaintenance({ numero }: { numero: string }) {
  const ajouter = useAjouterIntervention(numero);
  const types = useTypesEvenements();
  const [typeEvenement, definirType] = useState("");
  const [description, definirDescription] = useState("");
  const [dateEvenement, definirDateEvenement] = useState(aujourdHui());

  const typesDisponibles = (types.data?.data ?? []).filter(
    (type) => !TYPES_EXCLUS_DES_OPERATIONS.has(type.code),
  );

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault();
    await ajouter.mutateAsync({
      type_evenement: typeEvenement,
      description,
      date_evenement: versDateEvenementEnvoi(dateEvenement),
    });
    definirType("");
    definirDescription("");
    definirDateEvenement(aujourdHui());
  }

  return (
    <form className="formulaire-ligne" onSubmit={(e) => void soumettre(e)}>
      <label htmlFor="action-type-operation">Type</label>
      <select
        id="action-type-operation"
        required
        value={typeEvenement}
        onChange={(e) => definirType(e.target.value)}
      >
        <option value="" disabled>
          {types.isPending ? "Chargement…" : "Choisir un type"}
        </option>
        {typesDisponibles.map((type) => (
          <option key={type.code} value={type.code}>
            {type.libelle}
          </option>
        ))}
      </select>
      <label htmlFor="action-description-operation">Description</label>
      <input
        id="action-description-operation"
        required
        minLength={1}
        value={description}
        onChange={(e) => definirDescription(e.target.value)}
      />
      <label htmlFor="action-date-operation">Date de l'événement</label>
      <input
        id="action-date-operation"
        type="date"
        required
        max={aujourdHui()}
        value={dateEvenement}
        onChange={(e) => definirDateEvenement(e.target.value)}
      />
      <button type="submit" disabled={ajouter.isPending}>
        Ajouter
      </button>
      {ajouter.isError && (
        <span role="alert" className="formulaire-ligne__erreur">
          {ajouter.error instanceof ErreurApi ? ajouter.error.message : "L'ajout a échoué."}
        </span>
      )}
    </form>
  );
}

function FormulaireChangementTracker({ fiche }: { fiche: FicheGrv }) {
  const associer = useAssocierTracker(fiche.numero_grv);
  const [enEdition, definirEnEdition] = useState(false);
  const [numeroSerie, definirNumeroSerie] = useState("");

  async function associerTracker(evenement: React.FormEvent) {
    evenement.preventDefault();
    await associer.mutateAsync({ numero_serie_tracker: numeroSerie });
    definirEnEdition(false);
    definirNumeroSerie("");
  }

  async function desassocier() {
    await associer.mutateAsync({ numero_serie_tracker: null });
  }

  return (
    <div>
      <p className="carte__description">
        Tracker actuel : <strong>{fiche.numero_serie_tracker ?? "aucun"}</strong>. Un changement de
        tracker est enregistré dans la fiche de vie.
      </p>

      {enEdition ? (
        <form className="formulaire-ligne" onSubmit={(e) => void associerTracker(e)}>
          <label htmlFor="action-numero-serie-tracker">Nouveau numéro de série</label>
          <input
            id="action-numero-serie-tracker"
            required
            minLength={1}
            value={numeroSerie}
            onChange={(e) => definirNumeroSerie(e.target.value)}
          />
          <button type="submit" disabled={associer.isPending}>
            Enregistrer
          </button>
          <button type="button" onClick={() => definirEnEdition(false)}>
            Annuler
          </button>
        </form>
      ) : (
        <div className="formulaire-actions">
          <button type="button" onClick={() => definirEnEdition(true)}>
            {fiche.numero_serie_tracker ? "Remplacer le tracker" : "Associer un tracker"}
          </button>
          {fiche.numero_serie_tracker && (
            <button type="button" onClick={() => void desassocier()} disabled={associer.isPending}>
              Désassocier
            </button>
          )}
        </div>
      )}

      {associer.isError && (
        <p role="alert" className="formulaire-ligne__erreur">
          {associer.error instanceof ErreurApi ? associer.error.message : "L'opération a échoué."}
        </p>
      )}
    </div>
  );
}

function FormulaireChangementStatut({ fiche }: { fiche: FicheGrv }) {
  const changer = useChangerStatut(fiche.numero_grv);
  const statuts = useStatutsOperationnels();
  const [enEdition, definirEnEdition] = useState(false);
  const [nouveauStatut, definirNouveauStatut] = useState(fiche.statut_operationnel);

  const statutsActifs = statuts.data?.data ?? [];
  const options = optionsAvecValeurCourante(statutsActifs, fiche.statut_operationnel);

  function ouvrir() {
    definirNouveauStatut(fiche.statut_operationnel);
    definirEnEdition(true);
  }

  async function soumettre(evenement: React.FormEvent) {
    evenement.preventDefault();
    await changer.mutateAsync({ statut_operationnel: nouveauStatut });
    definirEnEdition(false);
  }

  return (
    <div>
      <p className="carte__description">
        Statut actuel : <strong>{fiche.statut_operationnel}</strong>. Un changement de statut est
        enregistré dans la fiche de vie.
      </p>

      {enEdition ? (
        <form className="formulaire-ligne" onSubmit={(e) => void soumettre(e)}>
          <label htmlFor="action-nouveau-statut">Nouveau statut</label>
          <select
            id="action-nouveau-statut"
            required
            value={nouveauStatut}
            onChange={(e) => definirNouveauStatut(e.target.value)}
          >
            {options.map((option) => (
              <option key={option.code} value={option.code}>
                {option.libelle}
              </option>
            ))}
          </select>
          <button type="submit" disabled={changer.isPending}>
            Enregistrer
          </button>
          <button type="button" onClick={() => definirEnEdition(false)}>
            Annuler
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={ouvrir}
          disabled={statuts.isPending}
          title={statuts.isPending ? "Chargement des statuts…" : undefined}
        >
          Changer le statut
        </button>
      )}

      {changer.isError && (
        <p role="alert" className="formulaire-ligne__erreur">
          {changer.error instanceof ErreurApi ? changer.error.message : "L'opération a échoué."}
        </p>
      )}
    </div>
  );
}
